import React, { useState, useMemo, useEffect } from 'react';
import { get, round } from 'lodash';
import { Button, Typography } from '@telus-uds/components-web';
import { Formik, Form, Field } from 'formik';
import Delete from '@telus-uds/palette-allium/build/web/icons/Delete';
import Edit from '@telus-uds/palette-allium/build/web/icons/Edit';
import PropTypes from 'prop-types';

import Modal from '../../../design/shared/modal';

import {
  ModalFieldLabel,
  ModalFooter,
  Table,
  Tr,
  Td,
  Th,
  TdImp,
  TdLabel
} from './css';
import { formatCurrency } from '../../../utils/currencyFormatter';
import { fieldDirectRouting } from '../../../utils/constant';

// Function to get distinct item description line 2 for a given category
const getDeviceType = (list, category) => {
  const data = get(list, category, []);
  return Object.keys(data).map(name => name);
};

const getDeviceName = (list, category, deviceType) => {
  const data = get(list, category, []);
  const device = get(data, deviceType, []);
  return [...new Set(device.map(item => item['name']))];
};

const OcdrHardware = ({
  onChange,
  defaultValues,
  orderData,
  productsPriceData,
  productName
}) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [
    hardwareSelections,
    setHardwareSelections
  ] = useState([]);
  const [errors, setErrors] = useState();
  const [disableFieldOneTime, setDisableFieldOneTime] = useState(true);
  const [disableFieldMonthlyRecurring, setDisableFieldMonthlyRecurring] = useState(true);

  useEffect(() => {
    if(!hardwareSelections.length){
      setHardwareSelections(get(defaultValues, 'selections', []));
    }
  }, [orderData]);

  let categories = [
    'IP Phones',
    'Headsets'
  ];

  if(productName.includes(fieldDirectRouting)){
    categories = [
      'IP Phones',
      'Headsets',
      'ATAs, Paging Devices and Gateways'
    ];
  }

  const totals = useMemo(() => {
    let totalMonthlyRecurring = 0;
    let totalOneTimePrice = 0;
    hardwareSelections.forEach(item => {
      totalMonthlyRecurring += round(item.totalMonthlyRecurring, 2);
      totalOneTimePrice += round(item.totalOneTimePrice, 2);
    });
    return {
      totalMonthlyRecurringPrice: totalMonthlyRecurring,
      totalOneTimePrice: totalOneTimePrice
    };
  }, [hardwareSelections]);

  const adjustedTotalPrice = useMemo(() => {
    const totalMonthly = totals.totalMonthlyRecurringPrice;
    const totalOneTime = totals.totalOneTimePrice;
    return {
      totalMonthly,
      totalOneTime
    };
  }, [totals]);

  const showModal = () => {
    setModalData(null);
    setModalVisible(true);
  };

  const onCloseModal = () => {
    setModalVisible(false);
  };

  const addLicencingRow = row => {
    if (row.quantity <= 0) {
      setErrors(prevErrors => ({
        ...prevErrors,
        quantity: 'Quantity of Licenses must be greater than 0'
      }));
      return;
    }
    setHardwareSelections([...hardwareSelections, row]);
  };

  const editItem = (e, row) => {
    e.preventDefault();
    setModalData(row);
    setModalVisible(true);
  };

  const handleWheel = e => e.target.blur();

  const deleteItem = (e, idx) => {
    e.preventDefault();
    setHardwareSelections([
      ...hardwareSelections.slice(0, idx),
      ...hardwareSelections.slice(idx + 1)
    ]);
  };

  useEffect(() => {
    onChange({
      selections: hardwareSelections,
      adjustedTotalPrice
    });
  }, [hardwareSelections, adjustedTotalPrice]);

  return (
    <div>
      {!!hardwareSelections.length && (
        <Table>
          <Th><Typography>Category</Typography></Th>
          <Th><Typography>Device Type</Typography></Th>
          <Th><Typography>Device</Typography></Th>
          <Th><Typography>Monthly Recurring Price</Typography></Th>
          <Th><Typography>One-Time Price</Typography></Th>
          <Th colSpan={2}><Typography>Actions</Typography></Th>{' '}
          {/* Add this column for edit/delete buttons */}
          {hardwareSelections.map(
            (
              {
                category,
                hardware,
                deviceType,
                device,
                totalMonthlyRecurring,
                totalOneTimePrice
              },
              idx
            ) => (
              // eslint-disable-next-line react/no-array-index-key
              <Tr key={`${category}-${hardware}-${idx}`}>
                <Td><Typography>{category}</Typography></Td>
                <Td><Typography>{deviceType}</Typography></Td>
                <Td><Typography>{device}</Typography></Td>
                <Td><Typography>${totalMonthlyRecurring}</Typography></Td>
                <Td><Typography>${totalOneTimePrice}</Typography></Td>
                <Td>
                  <Button
                    icon={Edit}
                    onClick={e =>
                      editItem(e, hardwareSelections[idx])
                    }
                    variant={{ text: true }}
                  />
                </Td>
                <Td>
                  <Button
                    icon={Delete}
                    onClick={e => deleteItem(e, idx)}
                    variant={{ text: true }}
                  />
                </Td>
              </Tr>
            )
          )}
          <Tr>
            <TdLabel colSpan={3}>Total</TdLabel>
            <TdImp><Typography>{formatCurrency(totals.totalMonthlyRecurringPrice)}</Typography></TdImp>
            <TdImp><Typography>{formatCurrency(totals.totalOneTimePrice)}</Typography></TdImp>
            <Td />
          </Tr>
        </Table>
      )}
      <Button onClick={showModal}>Add Hardware</Button>
      <Modal
        open={isModalVisible}
        setOpen={onCloseModal}
        size="large"
        header={modalData ? 'Edit Hardware' : 'Add Hardware'}
      >
        <Formik
          initialValues={
            modalData || {
              category: '',
              deviceType: '',
              device: '',
              monthlyRecurringPrice: 0,
              oneTimePrice: 0,
              quantity: 0,
              totalMonthlyRecurring: 0,
              totalOneTimePrice: 0
            }
          }
          onSubmit={values => {
            values['totalMonthlyRecurring'] =
              values['monthlyRecurringPrice'] * values['quantity'];
            values['totalOneTimePrice'] =
              values['oneTimePrice'] * values['quantity'];
            if (modalData) {
              // Update existing row
              const updatedSelections = [...hardwareSelections];
              updatedSelections[
                hardwareSelections.indexOf(modalData)
              ] = values;
              setHardwareSelections(updatedSelections);
            } else {
              // Add new row
              addLicencingRow(values);
            }
            if (values.quantity !== 0 && (!errors || !errors.quantity)) {
              onCloseModal();
            } else {
              showModal();
            }
          }}
        >
          {({ values, setFieldValue, handleSubmit }) => (
            <Form>
              <div>
                <ModalFieldLabel htmlFor="hardware">Category:</ModalFieldLabel>
                <Field
                  as="select"
                  id="category"
                  name="category"
                  className="frg-input-field color-light"
                  onChange={e => {
                    const selectedCategory = e.target.value;
                    setFieldValue('category', selectedCategory);
                    setFieldValue('hardware', ''); // Reset Subscription Line Option
                  }}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Field>
              </div>

              <div>
                <ModalFieldLabel htmlFor="hardware">
                  Device Type:
                </ModalFieldLabel>
                <Field
                  as="select"
                  id="deviceType"
                  name="deviceType"
                  className="frg-input-field color-light"
                  onChange={e => {
                    const selectedOption = e.target.value;
                    setFieldValue('deviceType', selectedOption);
                  }}
                  required
                >
                  <option value="">Select Device Type Option</option>
                  {values.category &&
                    getDeviceType(productsPriceData, values.category).map(
                      itemDesc => (
                        <option key={itemDesc} value={itemDesc}>
                          {itemDesc}
                        </option>
                      )
                    )}
                </Field>
              </div>

              <div>
                <ModalFieldLabel htmlFor="hardware">Device:</ModalFieldLabel>
                <Field
                  as="select"
                  id="device"
                  name="device"
                  className="frg-input-field color-light"
                  onChange={e => {
                    const selectedOption = e.target.value;
                    setFieldValue('device', selectedOption);
                    const data = get(productsPriceData, values.category, []);
                    const device = get(data, values.deviceType, []);

                    device.forEach(item => {
                      if (item['name'] === e.target.value) {
                        if (item['priceType'] === 'oneTime') {
                          setFieldValue('oneTimePrice', item['price']);
                          setDisableFieldOneTime(false);
                        } else {
                          setFieldValue('monthlyRecurringPrice', item['price']);
                          setDisableFieldMonthlyRecurring(false);
                        }
                      }
                    });
                  }}
                  required
                >
                  <option value="">Select Device Option</option>
                  {values.category &&
                    getDeviceName(
                      productsPriceData,
                      values.category,
                      values.deviceType
                    ).map(itemDesc => (
                      <option key={itemDesc} value={itemDesc}>
                        {itemDesc}
                      </option>
                    ))}
                </Field>
              </div>

              <div>
                <ModalFieldLabel htmlFor="cost">
                  Monthly Recurring Price:
                </ModalFieldLabel>
                <Field
                  disabled={disableFieldMonthlyRecurring ? true : false}
                  type="number"
                  id="monthlyRecurringPrice"
                  name="monthlyRecurringPrice"
                  className="frg-input-field color-light"
                  value={values.monthlyRecurringPrice}
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                  onChange={e => {
                    const { value } = e.target;
                    setFieldValue('monthlyRecurringPrice', value);
                  }}
                  required
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="price">
                  One-Time Price:
                </ModalFieldLabel>
                <Field
                  disabled={disableFieldOneTime ? true : false}
                  type="number"
                  id="oneTimePrice"
                  name="oneTimePrice"
                  className="frg-input-field color-light"
                  value={values.oneTimePrice}
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                  onChange={e => {
                    const { value } = e.target;
                    setFieldValue('oneTimePrice', value);
                  }}
                  required
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="quantity">Quantity:</ModalFieldLabel>
                <Field
                  type="number"
                  id="quantity"
                  name="quantity"
                  className="frg-input-field color-light"
                  onWheel={handleWheel}
                  onChange={e => {
                    const { value } = e.target;
                    setFieldValue('quantity', value);
                    setErrors(prevErrors => ({
                      ...prevErrors,
                      quantity:
                        value > 0
                          ? ''
                          : 'Quantity of Licenses must be greater than 0'
                    }));
                  }}
                  style={{ width: '100%' }}
                />
              </div>
              {errors && errors.quantity && (
                <div style={{ color: 'red' }}><Typography>{errors.quantity}</Typography></div>
              )}
              <ModalFooter>
                <Button type="button" onClick={handleSubmit}>Add</Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

OcdrHardware.propTypes = {
  onChange: PropTypes.func.isRequired,
  defaultValues: PropTypes.shape({}).isRequired,
  orderData: PropTypes.shape({}).isRequired
};

export default OcdrHardware;
